import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Smartphone, Server, Palette, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const iconMap = {
  code: Code,
  smartphone: Smartphone,
  server: Server,
  palette: Palette
};

const ServiceCard = ({ service, index }) => {
  const Icon = iconMap[service.icon];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
    >
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative h-full p-8 bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-2xl border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-500 overflow-hidden"
      >
        {/* Glow effect on hover */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: 'radial-gradient(circle at top left, rgba(0, 217, 255, 0.05), transparent 60%)'
          }}
        />

        {/* Icon with gradient background */}
        <motion.div
          whileHover={{ rotate: 5, scale: 1.1 }}
          transition={{ duration: 0.3 }}
          className="relative w-16 h-16 mb-6 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center border border-cyan-500/20"
        >
          <Icon className="w-8 h-8 text-cyan-400" />
        </motion.div>

        <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors text-balance">
          {service.title}
        </h3>
        <p className="text-gray-400 leading-relaxed mb-6 text-pretty">
          {service.description}
        </p>

        {/* Features list */}
        <ul className="space-y-2 mb-6">
          {service.features.map((feature, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * i }}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <div className="w-1 h-1 rounded-full bg-cyan-400" />
              {feature}
            </motion.li>
          ))}
        </ul>

        {/* Arrow indicator */}
        <motion.div
          className="flex items-center gap-2 text-cyan-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <span className="text-sm">En savoir plus</span>
          <ArrowRight className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.getServices().then(response => {
      // Transform services to match component structure
      const transformed = response.data.map(s => ({
        id: s.id,
        title: language === 'fr' ? s.title : s.titleEn,
        description: language === 'fr' ? s.description : s.descriptionEn,
        icon: s.icon,
        features: language === 'fr' ? s.features : s.featuresEn
      }));
      setServices(transformed);
    }).catch(err => console.error('Error loading services:', err));
  }, [language]);

  return (
    <section id="services" className="relative py-32 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f10] overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
          >
            <span className="text-sm text-cyan-400 font-medium font-mono">
              {language === 'fr' ? 'SERVICES' : 'SERVICES'}
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance hyphens-none px-1">
            {language === 'fr' ? 'Ce que' : 'What I'}{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {language === 'fr' ? 'je propose' : 'Offer'}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto text-pretty px-1">
            {language === 'fr'
              ? 'Des solutions techniques sur mesure pour transformer vos idées en produits digitaux performants'
              : 'Custom technical solutions to transform your ideas into high-performing digital products'
            }
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
